import { useEffect, useState } from "react";
import * as React from "react";
import { toast } from "sonner";
import { ArrowLeftRight, FileUp, Landmark, Link2, Minus, Plus, Unlink, Wallet } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";

const ACCOUNT_TYPES = { cash: "Cash", petty_cash: "Petty Cash", current: "Current Account", savings: "Savings Account", credit_card: "Credit Card", other: "Other" };

function ReconTab({ accounts }) {
  const [accId, setAccId] = useState("");
  const [data, setData] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [matchSel, setMatchSel] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);

  const load = () => {
    if (!accId) { setData(null); return; }
    setData(null);
    api.get(`/cashbank/${accId}/statement`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [accId]); // eslint-disable-line

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const upload = async () => {
    if (!csvText.trim()) { toast.error("Choose or paste a statement CSV"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/cashbank/${accId}/statement`, { csv_text: csvText });
      toast.success(`Imported ${r.imported} lines — ${r.auto_matched} auto-matched`);
      setCsvText("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const manualMatch = async (lineId) => {
    const txnId = matchSel[lineId];
    if (!txnId) { toast.error("Pick a voucher to match"); return; }
    try {
      await api.post(`/cashbank/statement/${lineId}/match`, { txn_id: txnId });
      toast.success("Matched");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const unmatch = async (lineId) => {
    try {
      await api.post(`/cashbank/statement/${lineId}/unmatch`);
      toast.success("Unmatched");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Select value={accId} onValueChange={setAccId}>
          <SelectTrigger data-testid="recon-account" className="w-64 bg-white dark:bg-slate-900"><SelectValue placeholder="Select account to reconcile" /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
        </Select>
        {accId && (
          <>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onFile} />
            <Button variant="outline" size="sm" data-testid="recon-file-btn" onClick={() => fileRef.current?.click()}>
              <FileUp className="mr-1.5 h-3.5 w-3.5" /> Statement CSV
            </Button>
            <Button size="sm" data-testid="recon-upload-btn" onClick={upload} disabled={busy || !csvText}
              className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Importing..." : "Import & Match"}</Button>
          </>
        )}
      </div>
      {csvText && <p className="mb-3 text-xs text-emerald-600 font-mono">{csvText.trim().split("\n").length - 1} statement lines ready</p>}
      {!accId ? (
        <p className="py-12 text-center text-sm text-slate-400">Choose an account above, then upload its bank statement (columns: date, description, debit, credit, reference).</p>
      ) : !data ? <TableSkeleton /> : (
        <>
          <div className="mb-4 flex flex-wrap gap-2" data-testid="recon-summary">
            <span className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs">Lines: <b className="font-mono">{data.summary.total_lines}</b></span>
            <span className="rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/5 px-3 py-1.5 text-xs text-emerald-700 dark:text-emerald-400">Matched: <b className="font-mono">{data.summary.matched}</b></span>
            <span className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/5 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-400">Unmatched: <b className="font-mono">{data.summary.unmatched}</b> (<Money value={data.summary.unmatched_amount} />)</span>
            <span className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs">Unreconciled vouchers: <b className="font-mono">{data.unmatched_txns.length}</b></span>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {data.items.length === 0 ? (
              <EmptyState testId="recon-empty" title="No statement lines" body="Import a statement CSV to start reconciling." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="recon-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">Date</th><th className="p-3">Description</th><th className="p-3 text-right">Amount</th>
                    <th className="p-3">Status</th><th className="p-3">Matched Voucher</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {data.items.map((l) => (
                      <tr key={l.id} data-testid={`recon-line-${l.id}`} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="p-3 font-mono text-xs">{l.date}</td>
                        <td className="p-3 max-w-[240px] truncate">{l.description}</td>
                        <td className={`p-3 text-right font-mono font-semibold ${l.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {l.amount >= 0 ? "+" : "−"}<Money value={Math.abs(l.amount)} />
                        </td>
                        <td className="p-3">
                          <Badge className={`border-0 text-[10px] ${l.matched ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"}`}>
                            {l.matched ? "Matched" : "Unmatched"}
                          </Badge>
                        </td>
                        <td className="p-3 font-mono text-xs">{l.matched_txn || "—"}</td>
                        <td className="p-3">
                          {l.matched ? (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" data-testid={`recon-unmatch-${l.id}`} onClick={() => unmatch(l.id)}>
                              <Unlink className="mr-1 h-3 w-3" /> Unmatch
                            </Button>
                          ) : (
                            <div className="flex gap-1.5 items-center justify-end">
                              <Select value={matchSel[l.id] || ""} onValueChange={(v) => setMatchSel((m) => ({ ...m, [l.id]: v }))}>
                                <SelectTrigger data-testid={`recon-matchsel-${l.id}`} className="h-8 w-44 text-xs"><SelectValue placeholder="Voucher..." /></SelectTrigger>
                                <SelectContent>
                                  {data.unmatched_txns.filter((t) => {
                                    const signed = ["deposit", "transfer_in"].includes(t.type) ? t.amount : -t.amount;
                                    return signed === l.amount;
                                  }).map((t) => <SelectItem key={t.id} value={t.id} className="text-xs">{t.txn_no} · {t.date}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`recon-match-${l.id}`} onClick={() => manualMatch(l.id)}>
                                <Link2 className="mr-1 h-3 w-3" /> Match
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const TXN_LABEL = { deposit: "Deposit", withdrawal: "Withdrawal", transfer_in: "Transfer In", transfer_out: "Transfer Out" };
const TXN_COLOR = { deposit: "text-emerald-600", transfer_in: "text-emerald-600", withdrawal: "text-red-600", transfer_out: "text-red-600" };

export default function CashBank() {
  const { activeId } = useCompany();
  const [accounts, setAccounts] = useState(null);
  const [txns, setTxns] = useState(null);
  const [accDialog, setAccDialog] = useState(false);
  const [txnDialog, setTxnDialog] = useState({ open: false, account: null, type: "deposit" });
  const [transferDialog, setTransferDialog] = useState(false);
  const [accForm, setAccForm] = useState({ name: "", type: "current", account_no: "", ifsc: "", bank_name: "", opening_balance: 0 });
  const [txnForm, setTxnForm] = useState({ date: new Date().toISOString().slice(0, 10), amount: "", reference: "", notes: "", employee: "" });
  const [trForm, setTrForm] = useState({ from_id: "", to_id: "", amount: "", date: new Date().toISOString().slice(0, 10), reference: "", notes: "" });
  const [filterAcc, setFilterAcc] = useState("all");

  const load = () => {
    api.get("/cashbank/accounts").then((r) => setAccounts(r.data)).catch((e) => toast.error(errMsg(e)));
    const qs = filterAcc !== "all" ? `?account_id=${filterAcc}` : "";
    api.get(`/cashbank/transactions${qs}`).then((r) => setTxns(r.data)).catch(() => setTxns({ items: [] }));
  };
  useEffect(() => { if (activeId) { setAccounts(null); setTxns(null); load(); } }, [activeId, filterAcc]); // eslint-disable-line

  const addAccount = async () => {
    if (!accForm.name.trim()) { toast.error("Account name required"); return; }
    try {
      await api.post("/cashbank/accounts", { ...accForm, opening_balance: +accForm.opening_balance || 0 });
      toast.success("Account added");
      setAccDialog(false); setAccForm({ name: "", type: "current", account_no: "", ifsc: "", bank_name: "", opening_balance: 0 });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addTxn = async () => {
    if (!(+txnForm.amount > 0)) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/cashbank/${txnDialog.account.id}/transaction`, { ...txnForm, type: txnDialog.type, amount: +txnForm.amount });
      toast.success(`${TXN_LABEL[txnDialog.type]} recorded`);
      setTxnDialog({ open: false, account: null, type: "deposit" });
      setTxnForm({ date: new Date().toISOString().slice(0, 10), amount: "", reference: "", notes: "", employee: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doTransfer = async () => {
    if (!trForm.from_id || !trForm.to_id || !(+trForm.amount > 0)) { toast.error("Select both accounts and an amount"); return; }
    try {
      await api.post("/cashbank/transfer", { ...trForm, amount: +trForm.amount });
      toast.success("Transfer completed");
      setTransferDialog(false);
      setTrForm({ from_id: "", to_id: "", amount: "", date: new Date().toISOString().slice(0, 10), reference: "", notes: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const petty = accounts?.items.find((a) => a.type === "petty_cash");

  return (
    <div data-testid="cashbank-page">
      <PageHeader title="Cash & Bank" testId="cashbank-title"
        sub={accounts ? `Total balance: ₹${(accounts.total_balance || 0).toLocaleString("en-IN")} across ${accounts.items.length} accounts` : "Accounts, transfers and petty cash"}>
        <Button variant="outline" data-testid="transfer-btn" onClick={() => setTransferDialog(true)}>
          <ArrowLeftRight className="mr-1.5 h-4 w-4" /> Transfer
        </Button>
        <Button data-testid="new-account-btn" onClick={() => setAccDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> Add Account</Button>
      </PageHeader>
      {accounts && <p className="hidden" data-testid="cashbank-total">{accounts.total_balance}</p>}

      {!accounts ? <TableSkeleton rows={3} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="accounts-grid">
          {accounts.items.map((a) => (
            <Card key={a.id} data-testid={`account-card-${a.name.replace(/\s+/g, "-").toLowerCase()}`}
              className="border-slate-200/80 dark:border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm">{a.name}</p>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400">{ACCOUNT_TYPES[a.type]}</p>
                    <Money value={a.current_balance} className="block mt-2 text-xl font-bold" />
                    {a.account_no && <p className="text-xs font-mono text-slate-400 mt-1">{a.bank_name} ···{a.account_no.slice(-4)}</p>}
                  </div>
                  <Landmark className="h-5 w-5 text-slate-300 dark:text-slate-600" />
                </div>
                <div className="mt-3 flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`deposit-${a.id}`}
                    onClick={() => setTxnDialog({ open: true, account: a, type: "deposit" })}><Plus className="h-3 w-3 mr-1" /> In</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`withdraw-${a.id}`}
                    onClick={() => setTxnDialog({ open: true, account: a, type: "withdrawal" })}><Minus className="h-3 w-3 mr-1" /> Out</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="transactions">
        <TabsList className="mb-4">
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="petty" data-testid="tab-petty-cash"><Wallet className="mr-1.5 h-3.5 w-3.5" /> Petty Cash</TabsTrigger>
          <TabsTrigger value="recon" data-testid="tab-reconciliation"><Link2 className="mr-1.5 h-3.5 w-3.5" /> Reconciliation</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <div className="mb-3">
            <Select value={filterAcc} onValueChange={setFilterAcc}>
              <SelectTrigger data-testid="txn-account-filter" className="w-56 bg-white dark:bg-slate-900"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {(accounts?.items || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!txns ? <TableSkeleton /> : txns.items.length === 0 ? (
              <EmptyState testId="txns-empty" title="No transactions yet" body="Use In / Out on an account card, or Transfer between accounts." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="txns-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">Voucher</th><th className="p-3">Date</th><th className="p-3">Account</th>
                    <th className="p-3">Type</th><th className="p-3">Notes</th><th className="p-3 text-right">Amount</th>
                    <th className="p-3 text-right">Balance After</th></tr></thead>
                  <tbody>
                    {txns.items.map((t) => (
                      <tr key={t.id} data-testid={`txn-row-${t.txn_no}`} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="p-3 font-mono text-xs">{t.txn_no}</td>
                        <td className="p-3">{fmtDate(t.date)}</td>
                        <td className="p-3">{t.account_name}</td>
                        <td className="p-3"><Badge variant="secondary" className="text-[10px]">{TXN_LABEL[t.type]}</Badge></td>
                        <td className="p-3 text-slate-500 text-xs max-w-[220px] truncate">{t.notes || t.reference || "—"}</td>
                        <td className={`p-3 text-right font-mono font-semibold ${TXN_COLOR[t.type]}`}>
                          {["deposit", "transfer_in"].includes(t.type) ? "+" : "−"}<Money value={t.amount} />
                        </td>
                        <td className="p-3 text-right"><Money value={t.balance_after} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="petty">
          {petty ? (
            <div className="max-w-3xl">
              <Card className="mb-4 border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/5">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Petty Cash Balance</p>
                    <Money value={petty.current_balance} className="text-2xl font-bold" data-testid="petty-balance" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" data-testid="petty-in-btn" onClick={() => setTxnDialog({ open: true, account: petty, type: "deposit" })}>Cash In</Button>
                    <Button size="sm" variant="outline" data-testid="petty-out-btn" onClick={() => setTxnDialog({ open: true, account: petty, type: "withdrawal" })}>Expense / Cash Out</Button>
                  </div>
                </CardContent>
              </Card>
              <p className="text-xs text-slate-400">Petty cash vouchers appear in the Transactions tab — filter by the Petty Cash account.</p>
            </div>
          ) : <TableSkeleton rows={2} />}
        </TabsContent>

        <TabsContent value="recon">
          <ReconTab accounts={accounts?.items || []} />
        </TabsContent>
      </Tabs>

      <Dialog open={accDialog} onOpenChange={setAccDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-md bg-white dark:bg-slate-900" data-testid="account-dialog">
          <DialogHeader><DialogTitle className="font-display">Add Account</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input data-testid="account-name" className="mt-1.5" value={accForm.name} onChange={(e) => setAccForm((f) => ({ ...f, name: e.target.value }))} placeholder="HDFC Current A/c" /></div>
            <div><Label>Type</Label>
              <Select value={accForm.type} onValueChange={(v) => setAccForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="account-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ACCOUNT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Bank Name</Label><Input className="mt-1.5" value={accForm.bank_name} onChange={(e) => setAccForm((f) => ({ ...f, bank_name: e.target.value }))} /></div>
            <div><Label>Account No</Label><Input className="mt-1.5 font-mono" value={accForm.account_no} onChange={(e) => setAccForm((f) => ({ ...f, account_no: e.target.value }))} /></div>
            <div><Label>IFSC</Label><Input className="mt-1.5 font-mono" value={accForm.ifsc} onChange={(e) => setAccForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))} /></div>
            <div><Label>Opening Balance (₹)</Label><Input data-testid="account-opening" type="number" className="mt-1.5" value={accForm.opening_balance} onChange={(e) => setAccForm((f) => ({ ...f, opening_balance: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccDialog(false)}>Cancel</Button>
            <Button data-testid="account-save-btn" onClick={addAccount} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Add Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={txnDialog.open} onOpenChange={() => setTxnDialog({ open: false, account: null, type: "deposit" })}>
        <DialogContent aria-describedby={undefined} className="max-w-md bg-white dark:bg-slate-900" data-testid="txn-dialog">
          <DialogHeader><DialogTitle className="font-display">
            {txnDialog.type === "deposit" ? "Cash / Bank In" : "Cash / Bank Out"} — {txnDialog.account?.name}
          </DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Current balance: <Money value={txnDialog.account?.current_balance || 0} className="font-semibold" /></p>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" className="mt-1.5" value={txnForm.date} onChange={(e) => setTxnForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><Label>Amount (₹) *</Label><Input data-testid="txn-amount" type="number" min="0" className="mt-1.5" value={txnForm.amount} onChange={(e) => setTxnForm((f) => ({ ...f, amount: e.target.value }))} /></div>
            <div><Label>Reference</Label><Input className="mt-1.5" value={txnForm.reference} onChange={(e) => setTxnForm((f) => ({ ...f, reference: e.target.value }))} /></div>
            <div><Label>Employee (if petty cash)</Label><Input className="mt-1.5" value={txnForm.employee} onChange={(e) => setTxnForm((f) => ({ ...f, employee: e.target.value }))} /></div>
            <div className="col-span-2"><Label>Notes</Label><Input data-testid="txn-notes" className="mt-1.5" value={txnForm.notes} onChange={(e) => setTxnForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Purpose / narration" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTxnDialog({ open: false, account: null, type: "deposit" })}>Cancel</Button>
            <Button data-testid="txn-save-btn" onClick={addTxn} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-md bg-white dark:bg-slate-900" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="font-display">Transfer Between Accounts</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>From *</Label>
              <Select value={trForm.from_id} onValueChange={(v) => setTrForm((f) => ({ ...f, from_id: v }))}>
                <SelectTrigger data-testid="transfer-from" className="mt-1.5"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>{(accounts?.items || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({`₹${(a.current_balance || 0).toLocaleString("en-IN")}`})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>To *</Label>
              <Select value={trForm.to_id} onValueChange={(v) => setTrForm((f) => ({ ...f, to_id: v }))}>
                <SelectTrigger data-testid="transfer-to" className="mt-1.5"><SelectValue placeholder="Destination" /></SelectTrigger>
                <SelectContent>{(accounts?.items || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" className="mt-1.5" value={trForm.date} onChange={(e) => setTrForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><Label>Amount (₹) *</Label><Input data-testid="transfer-amount" type="number" min="0" className="mt-1.5" value={trForm.amount} onChange={(e) => setTrForm((f) => ({ ...f, amount: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>Cancel</Button>
            <Button data-testid="transfer-save-btn" onClick={doTransfer} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
